import { AttachMoney, Sort, Tune } from "@mui/icons-material";
import { Avatar, Box, Checkbox, FormControlLabel, FormGroup, Radio, RadioGroup, Slider, TextField, Typography, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next";
import { HotelOrderByOptions, useHotelSearch } from "../Availability/Search/HotelSearchContext";
import { useCallback, useEffect, useState } from "react";
import { useDebouncedState } from "@frontend/features/common/hooks/useDebouncedState";


export const HotelAvailabilityFilters = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { updateSearchFilters, filters } = useHotelSearch();
  const [name, setName] = useState('');
  const [nameDebounced, setNameDebounced] = useDebouncedState<string>('', 500);

  const handleOrderByChange = (value: HotelOrderByOptions) => {
    updateSearchFilters({ orderBy: value });
  };

  const handlePriceRangeChange = (value: number[] | number) => {
    if (Array.isArray(value)) {
      updateSearchFilters({ minPrice: value[0], maxPrice: value[1] });
    }
  };

  const handleChainCodesChange = useCallback((value: string) => {
    const currentValues = filters.chainCodes;
    if (currentValues) {
      const updatedValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];

      updateSearchFilters({ chainCodes: updatedValues });
    }

  }, [filters]);

  const handleCategoryIdsChange = useCallback((value: string) => {
    const currentValues = filters.categoryIds;
    if (currentValues) {
      const updatedValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];

      updateSearchFilters({ categoryIds: updatedValues });
    }

  }, [filters]);

  const handleAmenitiesChange = useCallback((value: string) => {
    const currentValues = filters.amenities;
    if (currentValues) {
      const updatedValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];

      updateSearchFilters({ amenities: updatedValues });
    }

  }, [filters]);

  const handleHotelNameChange = (name: string) => {
    updateSearchFilters({
      hotelName: name,
    });
  };

  useEffect(() => {
    setNameDebounced(name);
  }, [name]);

  useEffect(() => {
    updateSearchFilters({
      hotelName: nameDebounced,
    });
  }, [nameDebounced]);

  const borderColor = `1px solid #ededed`; // Pongo este color pq el juli me pasas unos bordes con alphas que no le gustan nada a css TODO: hacerlo con unos div capaz?
  return (<Box
    justifyContent="space-between"
    p={`0, ${theme.spacing(3)}`}
    width={309}
  >
    <Box
      display="flex"
      pb={2}
      alignItems="center"
      gap={2}
      alignSelf="stretch"
    >
      <Avatar variant="rounded">
        <Tune />
      </Avatar>
      <Typography variant="h5">{t('common.filters')}</Typography>
    </Box>


    <Box
      display="flex"
      p={`${theme.spacing(2)} 0`}
      flexDirection="column"
      alignItems="flex-start"
      alignSelf="stretch"
      width={"100%"}
      sx={{
        borderTop: borderColor,
        borderBottom: borderColor
      }}
    >
      <Box
        display="flex"
        p={`${theme.spacing(1)} 0`}
        alignItems="center"
        gap={1}
      >
        <Sort /><Typography variant="subtitle2">{t('common.orderBy')}</Typography>
      </Box>

      <RadioGroup
        value={filters.orderBy}
        onChange={(e) => handleOrderByChange(e.target.value as HotelOrderByOptions)}
        name="orderBy"
      >
        <FormControlLabel value={"recomended"} control={<Radio />} label={t('common.recomended')} />
        <FormControlLabel value="cheapest" control={<Radio />} label={t('common.cheapest')} />
        <FormControlLabel value="not-economic" control={<Radio />} label={t('common.nonEconomic')} />
      </RadioGroup>

    </Box>

    <Box
      display="flex"
      p={`${theme.spacing(2)} 0`}
      flexDirection="column"
      alignItems="flex-start"
      alignSelf="stretch"
      width={"100%"}
      sx={{
        borderTop: borderColor,
        borderBottom: borderColor
      }}
    >

      <TextField
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('filters.searchName')}
        variant="outlined"
        fullWidth
      />

    </Box>

    <Box
      display="flex"
      p={`${theme.spacing(2)} 0`}
      flexDirection="column"
      alignItems="flex-start"
      alignSelf="stretch"
      width={"100%"}
      sx={{
        borderTop: borderColor,
        borderBottom: borderColor
      }}
    >
      <Box
        display="flex"
        p={`${theme.spacing(1)} 0`}
        alignItems="center"
        gap={1}
      >
        <AttachMoney /><Typography variant="subtitle2">{t('filters.priceRange')}</Typography>
      </Box>

      {(filters.minPrice && filters.maxPrice) && <Slider
        value={[filters.minPrice, filters.maxPrice]}
        min={filters.priceLimits?.min}
        max={filters.priceLimits?.max}
        onChange={(_e, newValue) => handlePriceRangeChange(newValue)}
        valueLabelDisplay="auto"
      />}

    </Box>

    <Box
      display="flex"
      p={`${theme.spacing(2)} 0`}
      flexDirection="column"
      alignItems="flex-start"
      alignSelf="stretch"
      width={"100%"}
      sx={{
        borderTop: borderColor,
        borderBottom: borderColor
      }}
    >
      <Box
        display="flex"
        p={`${theme.spacing(1)} 0`}
        alignItems="center"
        gap={1}
      >
        <AttachMoney /><Typography variant="subtitle2">{t('filters.hotelChains')}</Typography>
      </Box>

      <FormGroup>
        {filters.options?.chainCodes?.map(code => (
          <FormControlLabel
            key={code}
            control={
              <Checkbox
                checked={filters.chainCodes?.includes(code)} // Verifica si está seleccionado
                onChange={() => handleChainCodesChange(code)} // Actualiza el estado
              />
            }
            label={code}
          />
        ))}
      </FormGroup>

    </Box>

    {filters.categoryIds && filters.categoryIds.length > 0 && (
      <Box
        display="flex"
        p={`${theme.spacing(2)} 0`}
        flexDirection="column"
        alignItems="flex-start"
        alignSelf="stretch"
        width={"100%"}
        sx={{
          borderTop: borderColor,
          borderBottom: borderColor
        }}
      >
        <Box
          display="flex"
          p={`${theme.spacing(1)} 0`}
          alignItems="center"
          gap={1}
        >
          <AttachMoney /><Typography variant="subtitle2">{t('filters.categories')}</Typography>
        </Box>
        <FormGroup>
          {filters.options?.categoryIds?.map(code => (
            <FormControlLabel
              key={code}
              control={
                <Checkbox
                  checked={filters.categoryIds?.includes(code)}
                  onChange={() => handleCategoryIdsChange(code)}
                />
              }
              label={code}
            />
          ))}
        </FormGroup>
      </Box>
    )}

    {/* TODO: Esto lo dejo comentado pq son una banda, es una locura tenemos q pedir una lista de cuales quieren
          si le pongo todas las q son me freeza la pantalla */}

    {/* <Box
      display="flex"
      p={`${theme.spacing(2)} 0`}
      flexDirection="column"
      alignItems="flex-start"
      alignSelf="stretch"
      width={"100%"}
      sx={{
        borderTop: borderColor,
        borderBottom: borderColor
      }}
    >
      <Box
        display="flex"
        p={`${theme.spacing(1)} 0`}
        alignItems="center"
        gap={1}
      >
        <AttachMoney /><Typography variant="subtitle2">{t('filters.amenities')}</Typography>
      </Box>

      <FormGroup>
        {filters.options?.amenities?.map(code => (
          <FormControlLabel
            key={code}
            control={
              <Checkbox
                checked={filters.amenities?.includes(code)}
                onChange={() => handleAmenitiesChange(code)}
              />
            }
            label={code}
          />
        ))}
      </FormGroup>

    </Box> */}

  </Box >)
}
